import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { AccountComplianceDto, AssetDto, ComplianceControllerService, PairDto } from '@api/core';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withFeature,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { toOptionItemModel } from '@shared/components/multi-select-with-exclude/multi-select-with-exclude.utils';
import { DownloadStateService } from '@shared/services/download-state.service';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { useCurrentUser } from '@shared/store/features/current-user.feature';
import { saveAs } from 'file-saver';
import { keyBy, omit } from 'lodash';
import { EMPTY, mergeMap, pipe, switchMap } from 'rxjs';

interface PendingDownload {
  fileName: string;
  downloadKey?: string;
}

interface SharedComplianceState {
  accounts: AccountComplianceDto[];
  pairs: PairDto[];
  assets: AssetDto[];
  pendingDownloads: Record<number, PendingDownload>;
}

export const SharedComplianceStore = signalStore(
  { providedIn: 'root' },
  withState<SharedComplianceState>({
    accounts: [],
    pairs: [],
    assets: [],
    pendingDownloads: {},
  }),
  withProps(() => {
    const complianceService = inject(ComplianceControllerService);
    return {
      _complianceService: complianceService,
      _usersService: {
        getCurrentUser: () => complianceService.getCurrentUser1(),
      },
      _downloadStateService: inject(DownloadStateService),
      _notificationService: inject(NotificationService),
    };
  }),
  withFeature(useCurrentUser),
  withComputed(store => ({
    accountOptions: computed(() => store.accounts().map(a => toOptionItemModel(a, 'id', 'name'))),
    accountsMap: computed(() => keyBy(store.accounts(), 'id')),
    pairOptions: computed(() => store.pairs().map(p => toOptionItemModel(p, 'id', 'name'))),
    assetOptions: computed(() => store.assets().map(a => toOptionItemModel(a, 'id', 'code'))),
  })),
  withMethods(store => {
    const loadAccounts = rxMethod<void>(
      pipe(
        switchMap(() =>
          store._complianceService.getAccounts1().pipe(
            tapResponse({
              next: accounts => patchState(store, { accounts }),
              error: (error: HttpErrorResponse) =>
                store._notificationService.showErrorNotification(`Accounts couldn't be loaded`, error),
            }),
          ),
        ),
      ),
    );

    const loadPairs = rxMethod<void>(
      pipe(
        switchMap(() =>
          store._complianceService.getPairs1().pipe(
            tapResponse({
              next: pairs => patchState(store, { pairs }),
              error: (error: HttpErrorResponse) =>
                store._notificationService.showErrorNotification(`Pairs couldn't be loaded`, error),
            }),
          ),
        ),
      ),
    );

    const loadAssets = rxMethod<void>(
      pipe(
        switchMap(() =>
          store._complianceService.getAssets2().pipe(
            tapResponse({
              next: assets => patchState(store, { assets }),
              error: (error: HttpErrorResponse) =>
                store._notificationService.showErrorNotification(`Assets couldn't be loaded`, error),
            }),
          ),
        ),
      ),
    );

    const addPendingDownload = ({
      id,
      fileName,
      downloadKey,
    }: {
      id: number;
      fileName: string;
      downloadKey?: string;
    }) => {
      patchState(store, {
        pendingDownloads: {
          ...store.pendingDownloads(),
          [id]: { fileName, downloadKey },
        },
      });
    };

    const downloadPendingReport = rxMethod<number>(
      pipe(
        mergeMap(id => {
          const pending = store.pendingDownloads()[id];
          if (!pending) {
            return EMPTY;
          }

          const { fileName, downloadKey } = pending;
          patchState(store, { pendingDownloads: omit(store.pendingDownloads(), id) });
          if (downloadKey) {
            store._downloadStateService.success(downloadKey);
          }

          return store._complianceService.downloadReport1({ id }).pipe(
            tapResponse({
              next: blob => saveAs(blob, fileName),
              error: (error: HttpErrorResponse) =>
                store._notificationService.showErrorNotification(`The report couldn't be downloaded`, error),
            }),
          );
        }),
      ),
    );

    const failPendingReport = (id: number) => {
      const pending = store.pendingDownloads()[id];
      if (!pending) {
        return;
      }
      patchState(store, { pendingDownloads: omit(store.pendingDownloads(), id) });
      if (pending.downloadKey) {
        store._downloadStateService.error(pending.downloadKey);
      }
    };

    return {
      loadAccounts,
      loadPairs,
      loadAssets,
      addPendingDownload,
      downloadPendingReport,
      failPendingReport,
    };
  }),
  withHooks({
    onInit(store) {
      store.loadAccounts();
      store.loadPairs();
      store.loadAssets();
    },
  }),
);
